import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Paperclip, Send } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { EricInlineDrawer } from '@/components/eric/EricInlineDrawer'
import { useAssistantName } from '@/lib/use-assistant-name'
import type { EricSurface } from '@/lib/eric-surface'

/**
 * Unified text + voice composer. The single shared component every
 * freeform-text surface in the app should use — replaces the previous
 * five-variant patchwork (dashboard AskEric, Messaging continue
 * dialog, vehicle notes, customer notes, appointment notes) so
 * mechanics see ONE button row in ONE position with ONE gesture for
 * voice.
 *
 * Visual contract (mobile-first, per CLAUDE.md § "Responsive design
 * conventions"):
 *
 *   ┌──────────────────────────────────────────┐
 *   │  [Textarea — auto-grows, max 6 lines]    │
 *   ├──────────────────────────────────────────┤
 *   │  [📎 attach] [🎤 mic] [🤖 Asst]  [⏎ Send] │
 *   └──────────────────────────────────────────┘
 *
 * All buttons meet the 44×44px touch-target rule. Attach, mic and
 * assistant slots are *optional* — pass the relevant prop block to
 * enable, omit to hide. The Send/Save button is always rendered.
 *
 * Voice behavior is unified to "dictate-and-append" everywhere (Q4
 * recommendation): the mic appends the transcript to whatever's in
 * the textarea. The previous Appointments-only "Summarize with Eric"
 * step is replaced by an Eric drawer action so there's one mental
 * model across surfaces ("mic = dictate; Eric handles cleanup if
 * asked").
 */

export type ChatInputHandle = {
  /** Insert text at the current cursor. */
  insertAtCursor: (text: string) => void
  /** Replace the current text selection (or the whole value if no
   *  selection) with the provided text. */
  replaceSelection: (text: string) => void
  /** Focus the textarea programmatically. */
  focus: () => void
}

export type ChatInputProps = {
  value: string
  onChange: (next: string) => void
  onSubmit: () => void
  /** Custom button label; defaults to t('chat_input.send'). */
  submitLabel?: string
  /** Custom placeholder; defaults to t('chat_input.placeholder'). */
  placeholder?: string
  /** Disable all interactive elements. */
  disabled?: boolean
  /** True when submission is in-flight; shows a spinner in the Send
   *  button and disables Send + Enter submit. */
  submitting?: boolean
  /** Cmd/Ctrl+Enter submits when true. Default true. Plain Enter
   *  inserts a newline (textarea convention). */
  submitOnCmdEnter?: boolean
  /** Plain Enter submits when true (chat convention — the dock
   *  composer). Shift+Enter still inserts a newline. Default false. */
  submitOnEnter?: boolean
  /** Allow Send (button + keyboard) with an empty textarea. For
   *  surfaces where the payload can live outside the text — the dock's
   *  staged slot / staged photo. Default false. */
  allowEmptySubmit?: boolean
  /** Fires on Backspace with an empty textarea (and prevents the
   *  default). The dock composer peels its cascade breadcrumb here.
   *  Omit for the plain-textarea behavior. */
  onBackspaceEmpty?: () => void
  /** `inputMode` passthrough for the textarea (e.g. 'numeric' for the
   *  dock's mileage value editor). */
  inputMode?: React.ComponentProps<'textarea'>['inputMode']
  /** Attachment slot — omit to hide the paperclip. The component owns
   *  the hidden file input (and resets it so re-picking the same file
   *  fires again); the surface receives the picked File. */
  attachment?: {
    /** File-input accept filter. Default 'image/*'. */
    accept?: string
    onFile: (file: File) => void
    /** Aria-label + tooltip for the paperclip button. Callers pass
     *  their own localized string so this stays namespace-free. */
    ariaLabel: string
  }
  /** The composer's mic — callers supply their surface's own mic
   *  (the dock mounts the voice-message push-to-talk). A recording
   *  strip may overlay this WHOLE composer — the root is `relative`
   *  for exactly that (VoiceMicButton's contract; matches the thread
   *  composer, where recording covers the textarea too). The old
   *  built-in dictation mic left with its last consumer — Messaging /
   *  VehicleDetail mount PushToTalkMic directly. */
  micSlot?: React.ReactNode
  /** Eric slot — omit to hide the Eric button. Per Q6: never on
   *  field-level inputs (name, phone, VIN); only on freeform
   *  composer surfaces. */
  eric?: {
    surface: EricSurface
  }
  /** Min visible rows before scroll. Default 2. */
  minRows?: number
  /** Max visible rows before scroll. Default 6. */
  maxRows?: number
  /** Auto-focus on mount. Default false. */
  autoFocus?: boolean
  /** Max characters (matches the existing 500-char cap on the brain
   *  payload — useful for chat surfaces). */
  maxLength?: number
  /** Aria-label override (defaults to the placeholder). */
  ariaLabel?: string
}

export const ChatInput = forwardRef<ChatInputHandle, ChatInputProps>(function ChatInput(
  {
    value,
    onChange,
    onSubmit,
    submitLabel,
    placeholder,
    disabled,
    submitting,
    submitOnCmdEnter = true,
    submitOnEnter = false,
    allowEmptySubmit = false,
    onBackspaceEmpty,
    inputMode,
    attachment,
    micSlot,
    eric,
    minRows = 2,
    maxRows = 6,
    autoFocus,
    maxLength,
    ariaLabel,
  },
  ref,
) {
  const { t } = useTranslation('chat_input')
  const assistantName = useAssistantName()
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [ericOpen, setEricOpen] = useState(false)

  // Auto-grow the textarea between minRows and maxRows.
  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    // Reset height so we measure the natural content height, not the
    // previous expanded height.
    el.style.height = 'auto'
    // Read line-height in pixels for the row math. Falls back to 24px
    // if the computed style is unparseable for any reason (e.g.
    // 'normal'); that's a safe-ish default for text-base.
    const lineHeight = parseFloat(
      window.getComputedStyle(el).lineHeight || '24',
    ) || 24
    const minHeight = lineHeight * minRows
    const maxHeight = lineHeight * maxRows
    const next = Math.max(
      minHeight,
      Math.min(maxHeight, el.scrollHeight),
    )
    el.style.height = `${next}px`
    el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden'
  }, [value, minRows, maxRows])

  useImperativeHandle(
    ref,
    () => ({
      insertAtCursor: (text: string) => {
        const el = textareaRef.current
        if (!el) {
          onChange(`${value}${text}`)
          return
        }
        const start = el.selectionStart ?? value.length
        const end = el.selectionEnd ?? value.length
        const next = value.slice(0, start) + text + value.slice(end)
        onChange(next)
        // Restore caret after React commits the new value.
        requestAnimationFrame(() => {
          if (!textareaRef.current) return
          const pos = start + text.length
          textareaRef.current.focus()
          textareaRef.current.setSelectionRange(pos, pos)
        })
      },
      replaceSelection: (text: string) => {
        const el = textareaRef.current
        if (!el) {
          onChange(text)
          return
        }
        const start = el.selectionStart ?? 0
        const end = el.selectionEnd ?? value.length
        // If nothing is selected, treat as "replace everything" — the
        // drawer's "Replace selection" button is also used as the
        // catch-all "use this draft" affordance when the textarea is
        // empty.
        if (start === end) {
          onChange(text)
          requestAnimationFrame(() => {
            if (!textareaRef.current) return
            textareaRef.current.focus()
            textareaRef.current.setSelectionRange(text.length, text.length)
          })
          return
        }
        const next = value.slice(0, start) + text + value.slice(end)
        onChange(next)
        requestAnimationFrame(() => {
          if (!textareaRef.current) return
          const pos = start + text.length
          textareaRef.current.focus()
          textareaRef.current.setSelectionRange(pos, pos)
        })
      },
      focus: () => {
        textareaRef.current?.focus()
      },
    }),
    [onChange, value],
  )

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter') {
        // Shift+Enter always inserts a newline. Otherwise: plain Enter
        // submits on submitOnEnter surfaces; Cmd/Ctrl+Enter submits on
        // submitOnCmdEnter surfaces (submitOnEnter is a superset).
        const plainEnter = submitOnEnter && !e.shiftKey
        const cmdEnter = submitOnCmdEnter && (e.metaKey || e.ctrlKey)
        if (!plainEnter && !cmdEnter) return
        e.preventDefault()
        if (
          !submitting &&
          !disabled &&
          (allowEmptySubmit || value.trim().length > 0)
        ) {
          onSubmit()
        }
        return
      }
      if (e.key === 'Backspace' && value.length === 0 && onBackspaceEmpty) {
        e.preventDefault()
        onBackspaceEmpty()
      }
    },
    [
      submitOnEnter,
      submitOnCmdEnter,
      submitting,
      disabled,
      allowEmptySubmit,
      value,
      onSubmit,
      onBackspaceEmpty,
    ],
  )

  const placeholderText = placeholder ?? t('placeholder')
  const sendLabel = submitLabel ?? t('send')
  const canSubmit =
    !disabled &&
    !submitting &&
    (allowEmptySubmit || value.trim().length > 0)

  return (
    // `relative`: a micSlot's recording strip overlays THIS whole composer
    // (textarea included, absolute inset-0 — VoiceMicButton contract). The
    // thread composer covers its textarea the same way; covering only the
    // controls row would leave the textarea live mid-recording, and an
    // Enter there silently cancels the clip.
    <div className="relative space-y-2">
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={placeholderText}
        rows={minRows}
        maxLength={maxLength}
        inputMode={inputMode}
        disabled={disabled || submitting}
        autoFocus={autoFocus}
        aria-label={ariaLabel ?? placeholderText}
        // The auto-grow effect sets height inline; keep `resize-none`
        // so the user can't drag it taller and confuse the layout.
        className="resize-none"
      />

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {attachment ? (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept={attachment.accept ?? 'image/*'}
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) attachment.onFile(file)
                  // Reset so re-picking the same file fires onChange
                  // again (the dock's swap-the-photo flow).
                  e.currentTarget.value = ''
                }}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                disabled={disabled || submitting}
                onClick={() => fileInputRef.current?.click()}
                aria-label={attachment.ariaLabel}
                title={attachment.ariaLabel}
                // 44×44 floor, same as the mic below.
                className="min-h-11 min-w-11"
              >
                <Paperclip className="size-4" />
              </Button>
            </>
          ) : null}

          {micSlot}

          {eric ? (
            <Button
              type="button"
              variant="outline"
              disabled={disabled || submitting}
              onClick={() => setEricOpen(true)}
              className="min-h-11 gap-2 text-brand"
              aria-label={t('open_assistant', { name: assistantName })}
              title={t('open_assistant', { name: assistantName })}
            >
              {/* Robot glyph matches the inline ChatInput contract from
                  Mobile.md. Sparkles is reserved for the drawer header
                  itself so the two surfaces stay distinguishable. */}
              <span className="text-base leading-none">🤖</span>
              <span className="text-sm font-medium">{assistantName}</span>
            </Button>
          ) : null}
        </div>

        <Button
          type="button"
          onClick={onSubmit}
          disabled={!canSubmit}
          className="min-h-11 bg-brand text-white hover:bg-brand-dark hover:text-white"
        >
          {submitting ? (
            <>
              <Loader2 className="size-4 animate-spin" />
              {t('sending')}
            </>
          ) : (
            <>
              <Send className="size-4" />
              {sendLabel}
            </>
          )}
        </Button>
      </div>

      {eric ? (
        <EricInlineDrawer
          open={ericOpen}
          onOpenChange={setEricOpen}
          surface={eric.surface}
          currentText={value}
          onInsertAtCursor={(text) => {
            // Implemented inline rather than via the imperative handle
            // because the drawer holds a different ref. Same logic.
            const el = textareaRef.current
            if (!el) {
              onChange(`${value}${text}`)
              return
            }
            const start = el.selectionStart ?? value.length
            const end = el.selectionEnd ?? value.length
            const next = value.slice(0, start) + text + value.slice(end)
            onChange(next)
            requestAnimationFrame(() => {
              if (!textareaRef.current) return
              const pos = start + text.length
              textareaRef.current.focus()
              textareaRef.current.setSelectionRange(pos, pos)
            })
          }}
          onReplaceSelection={(text) => {
            const el = textareaRef.current
            if (!el) {
              onChange(text)
              return
            }
            const start = el.selectionStart ?? 0
            const end = el.selectionEnd ?? value.length
            if (start === end) {
              onChange(text)
            } else {
              onChange(value.slice(0, start) + text + value.slice(end))
            }
            requestAnimationFrame(() => {
              if (!textareaRef.current) return
              const pos =
                start === end ? text.length : start + text.length
              textareaRef.current.focus()
              textareaRef.current.setSelectionRange(pos, pos)
            })
          }}
        />
      ) : null}
    </div>
  )
})
